import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useVehicles } from '../hooks/useVehicles';
import { formatCurrency, formatDate } from '../utils/dateUtils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area, RadialBarChart, RadialBar
} from 'recharts';
import { Download, BarChart3, TrendingUp, PieChart as PieIcon, Clock } from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { FleetInsights } from '../components/FleetInsights';

const COLORS = [
  '#4f8ef7', '#7c3aed', '#22c55e', '#f97316', '#eab308', '#ef4444',
  '#06b6d4', '#a855f7', '#ec4899', '#14b8a6', '#f43f5e', '#84cc16',
  '#8b5cf6', '#fb923c', '#facc15', '#34d399', '#60a5fa', '#c084fc',
  '#f472b6', '#2dd4bf', '#fb7185', '#a3e635', '#818cf8', '#fdba74',
  '#fde047', '#6ee7b7', '#93c5fd', '#d8b4fe', '#fbcfe8', '#99f6e4',
  '#fca5a5', '#d9f99d', '#bfdbfe', '#e9d5ff', '#fde68a', '#bbf7d0',
  '#a5f3fc', '#fecdd3', '#ddd6fe', '#fed7aa', '#fef08a', '#a7f3d0',
  '#bae6fd', '#f5d0fe', '#fda4af', '#ecfccb', '#ccfbf1', '#ffe4e6',
  '#ede9fe', '#fef9c3',
];

const GRADIENTS = [
  ['#4f8ef7','#7c3aed'],
  ['#22c55e','#06b6d4'],
  ['#f97316','#ef4444'],
  ['#eab308','#f97316'],
  ['#a855f7','#ec4899'],
  ['#06b6d4','#4f8ef7'],
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '10px 14px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      }}>
        {label && <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 4 }}>{label}</div>}
        {payload.map((p, i) => (
          <div key={i} style={{ fontSize: 14, fontWeight: 700, color: p.color || 'var(--accent)' }}>
            {formatCurrency(p.value)}
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const CustomPieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '10px 14px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{payload[0].name}</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: payload[0].payload.fill }}>{formatCurrency(payload[0].value)}</div>
      </div>
    );
  }
  return null;
};

const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export const Reports = () => {
  const { vehicles } = useVehicles();
  const [allCosts, setAllCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('monthly');

  useEffect(() => {
    const load = async () => {
      const [e, i, r, m, s] = await Promise.all([
        supabase.from('expenses').select('*, vehicles(id,registration_number,brand,model)'),
        supabase.from('insurance_records').select('*, vehicles(id,registration_number,brand,model)'),
        supabase.from('road_tax_records').select('*, vehicles(id,registration_number,brand,model)'),
        supabase.from('mechanical_reports').select('*, vehicles(id,registration_number,brand,model)'),
        supabase.from('service_records').select('*, vehicles(id,registration_number,brand,model)'),
      ]);

      const combined = [
        ...(e.data || []).map(r => ({ date: r.expense_date, amount: Number(r.amount || 0), category: r.category, source: 'Manual', vehicle: r.vehicles, description: r.description || '—', supplier: r.supplier || '—' })),
        ...(i.data || []).filter(r => r.cost && Number(r.cost) > 0).map(r => ({ date: r.start_date || r.expiry_date, amount: Number(r.cost || 0), category: 'Insurance', source: 'Insurance', vehicle: r.vehicles, description: r.insurance_company ? `Insurance — ${r.insurance_company}` : 'Insurance', supplier: r.insurance_company || '—' })),
        ...(r.data || []).filter(r => r.amount_paid && Number(r.amount_paid) > 0).map(r => ({ date: r.payment_date || r.expiry_date, amount: Number(r.amount_paid || 0), category: 'Road Tax', source: 'Road Tax', vehicle: r.vehicles, description: 'Road Tax', supplier: '—' })),
        ...(m.data || []).filter(r => r.cost && Number(r.cost) > 0).map(r => ({ date: r.report_date, amount: Number(r.cost || 0), category: 'Mechanical', source: 'Mechanical', vehicle: r.vehicles, description: r.garage_name ? `Mechanical — ${r.garage_name}` : 'Mechanical', supplier: r.garage_name || '—' })),
        ...(s.data || []).filter(r => r.cost && Number(r.cost) > 0).map(r => ({ date: r.service_date, amount: Number(r.cost || 0), category: 'Service', source: 'Service', vehicle: r.vehicles, description: r.garage_name ? `Service — ${r.garage_name}` : 'Service', supplier: r.garage_name || '—' })),
      ].sort((a, b) => (!a.date ? 1 : !b.date ? -1 : new Date(b.date) - new Date(a.date)));

      setAllCosts(combined);
      setLoading(false);
    };
    load();
  }, []);

  const monthlyData = Array.from({ length: 6 }, (_, i) => {
    const month = subMonths(new Date(), 5 - i);
    const label = format(month, 'MMM yy');
    const start = format(startOfMonth(month), 'yyyy-MM-dd');
    const end = format(endOfMonth(month), 'yyyy-MM-dd');
    const total = allCosts.filter(r => r.date && r.date >= start && r.date <= end).reduce((s, r) => s + r.amount, 0);
    return { month: label, total: parseFloat(total.toFixed(2)) };
  });

  const catMap = {};
  allCosts.forEach(r => { catMap[r.category] = (catMap[r.category] || 0) + r.amount; });
  const categoryData = Object.entries(catMap).map(([name, value], i) => ({ name, value: parseFloat(value.toFixed(2)), fill: COLORS[i % COLORS.length] })).sort((a, b) => b.value - a.value);

  const vehicleMap = {};
  allCosts.forEach(r => {
    const key = r.vehicle?.registration_number || 'Unknown';
    vehicleMap[key] = (vehicleMap[key] || 0) + r.amount;
  });
  const vehicleData = Object.entries(vehicleMap).map(([name, total], i) => ({ name, total: parseFloat(total.toFixed(2)), fill: COLORS[i % COLORS.length] })).sort((a, b) => b.total - a.total);

  const totalAll = allCosts.reduce((s, r) => s + r.amount, 0);

  const exportCSV = () => {
    const rows = [['Date','Vehicle','Source','Category','Description','Amount','Supplier'], ...allCosts.map(r => [r.date, r.vehicle?.registration_number, r.source, r.category, r.description, r.amount, r.supplier])];
    const csv = rows.map(r => r.map(c => `"${c || ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `fleet-report-${format(new Date(), 'yyyy-MM-dd')}.csv`; a.click();
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Reports</div>
          <div className="page-subtitle">All fleet costs — Total: {formatCurrency(totalAll)}</div>
        </div>
        <button className="btn btn-ghost" onClick={exportCSV}><Download size={14} /> Export CSV</button>
      </div>

      {/* Summary cards with gradients */}
      <div className="stats-grid" style={{ marginBottom: 28 }}>
        {[
          { label: 'Total All-Time', value: formatCurrency(totalAll), grad: ['#4f8ef7','#7c3aed'] },
          { label: 'This Month', value: formatCurrency(monthlyData[5]?.total || 0), grad: ['#22c55e','#06b6d4'] },
          { label: 'Total Records', value: allCosts.length, grad: ['#f97316','#ef4444'] },
          { label: 'Active Vehicles', value: vehicles.length, grad: ['#a855f7','#ec4899'] },
        ].map((item, i) => (
          <div key={i} className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: `linear-gradient(135deg, ${item.grad[0]}, ${item.grad[1]})`,
              marginBottom: 12,
            }} />
            <div className="stat-value" style={{ background: `linear-gradient(135deg, ${item.grad[0]}, ${item.grad[1]})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              {item.value}
            </div>
            <div className="stat-label">{item.label}</div>
          </div>
        ))}
      </div>

      <div className="tabs">
        {[
          {
            key: 'monthly',
            label: 'Monthly Trend',
            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
          },
          {
            key: 'by-vehicle',
            label: 'By Vehicle',
            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
          },
          {
            key: 'by-category',
            label: 'By Category',
            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg>,
          },
          {
            key: 'insights',
            label: 'Fleet Insights',
            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M18 17V9M13 17V5M8 17v-3"/></svg>,
          },
          {
            key: 'history',
            label: 'History',
            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
          },
        ].map(t => (
          <div key={t.key} className={`tab${activeTab === t.key ? ' active' : ''}`} onClick={() => setActiveTab(t.key)}>
            {t.icon}{t.label}
          </div>
        ))}
      </div>

      {activeTab === 'monthly' && (
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 16 }}>Monthly Cost Trend</div>
          <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 24 }}>Last 6 months — all cost sources combined</div>

          {/* Area chart */}
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4f8ef7" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#4f8ef7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: 'var(--text3)', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text3)', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={v => `€${v}`} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
              <Area type="monotone" dataKey="total" stroke="#4f8ef7" strokeWidth={3} fill="url(#colorTotal)" dot={{ fill: '#4f8ef7', strokeWidth: 2, r: 5 }} activeDot={{ r: 8, fill: '#4f8ef7' }} />
            </AreaChart>
          </ResponsiveContainer>

          {/* Month breakdown cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginTop: 20 }}>
            {monthlyData.map((m, i) => (
              <div key={i} style={{ textAlign: 'center', background: 'var(--surface2)', borderRadius: 8, padding: '10px 6px', border: i === 5 ? '1px solid var(--accent)' : '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>{m.month}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: i === 5 ? 'var(--accent)' : 'var(--text)' }}>{formatCurrency(m.total)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'by-vehicle' && (
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 16 }}>Total Cost by Vehicle</div>
          <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 24 }}>All-time cost of ownership per vehicle</div>

          {vehicleData.length === 0 ? (
            <div className="empty-state"><BarChart3 /><h3>No data yet</h3></div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={vehicleData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    {vehicleData.map((_, i) => (
                      <linearGradient key={i} id={`vGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={GRADIENTS[i % GRADIENTS.length][0]} stopOpacity={1} />
                        <stop offset="100%" stopColor={GRADIENTS[i % GRADIENTS.length][1]} stopOpacity={0.8} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: 'var(--text3)', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'var(--text3)', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={v => `€${v}`} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                    {vehicleData.map((_, i) => (
                      <Cell key={i} fill={`url(#vGrad${i})`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {/* Vehicle ranking */}
              <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {vehicleData.map((v, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: COLORS[i % COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                      {i + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 13, fontFamily: 'DM Mono,monospace' }}>{v.name}</span>
                        <span style={{ fontWeight: 700, color: COLORS[i % COLORS.length] }}>{formatCurrency(v.total)}</span>
                      </div>
                      <div style={{ height: 6, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${totalAll > 0 ? (v.total / vehicleData[0].total) * 100 : 0}%`, background: COLORS[i % COLORS.length], borderRadius: 3, transition: 'width 0.6s ease' }} />
                      </div>
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--text2)', width: 40, textAlign: 'right' }}>
                      {totalAll > 0 ? ((v.total / totalAll) * 100).toFixed(1) + '%' : '—'}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'by-category' && (
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 16 }}>Cost by Category</div>
          <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 24 }}>Breakdown of all fleet costs by type</div>

          {categoryData.length === 0 ? (
            <div className="empty-state"><BarChart3 /><h3>No data yet</h3></div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }} className="reports-pie-grid">
              {/* Donut chart */}
              <div>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={130}
                      dataKey="value"
                      nameKey="name"
                      labelLine={false}
                      label={renderCustomLabel}
                      strokeWidth={2}
                      stroke="var(--bg)"
                    >
                      {categoryData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>

                {/* Centre total */}
                <div style={{ textAlign: 'center', marginTop: -20 }}>
                  <div style={{ fontSize: 13, color: 'var(--text2)' }}>TOTAL</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>{formatCurrency(totalAll)}</div>
                </div>
              </div>

              {/* Category breakdown */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'center' }}>
                {categoryData.map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: COLORS[i % COLORS.length] }}>{formatCurrency(c.value)}</span>
                      </div>
                      <div style={{ height: 4, background: 'var(--surface2)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${totalAll > 0 ? (c.value / totalAll) * 100 : 0}%`, background: COLORS[i % COLORS.length], borderRadius: 2 }} />
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text2)', width: 36, textAlign: 'right' }}>
                      {totalAll > 0 ? ((c.value / totalAll) * 100).toFixed(1) + '%' : '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'insights' && <FleetInsights />}

      {activeTab === 'history' && (
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 16 }}>Full Cost History</div>
          <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16 }}>{allCosts.length} total records</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Date</th><th>Vehicle</th><th>Source</th><th>Category</th><th>Description</th><th>Amount</th><th>Supplier</th></tr>
              </thead>
              <tbody>
                {allCosts.map((r, i) => (
                  <tr key={i}>
                    <td>{formatDate(r.date)}</td>
                    <td className="td-primary" style={{ fontFamily: 'DM Mono,monospace', fontSize: 12 }}>{r.vehicle?.registration_number}</td>
                    <td><span className="badge badge-blue">{r.source}</span></td>
                    <td><span className="badge badge-gray">{r.category}</span></td>
                    <td>{r.description}</td>
                    <td style={{ color: 'var(--orange)', fontWeight: 700 }}>{formatCurrency(r.amount)}</td>
                    <td>{r.supplier}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};